import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui";
import { ChangelogCard, DistributionTabs } from "@/components/changelogs";
import { useChangelogs, useDownloadChangelog } from "@/hooks/changelogs";
import { useAvailableProducts } from "@/hooks/products";
import { DashboardPageLayout, DashboardPageLoading } from "@/layouts/DashboardPageLayout";
export default function ChangelogsPage() {
    const [limit] = useState(50);
    const [selectedView, setSelectedView] = useState("appliance");
    const [isDownloading, setIsDownloading] = useState(false);
    const [expandedSections, setExpandedSections] = useState(new Set());
    const { data: availableProducts, isPending: isProductAccessPending } = useAvailableProducts();
    const canAccessApplianceSoftware = availableProducts?.products.some((product)=>product.isLicensed) ?? false;
    const view = canAccessApplianceSoftware ? selectedView : "blast";
    const canLoadChangelogs = !isProductAccessPending;
    const { changelogs: applianceChangelogs, isLoading: isApplianceLoading, isFetching: isApplianceFetching, error: applianceError, refetch: refetchAppliance } = useChangelogs(limit, undefined, "standard", canLoadChangelogs && canAccessApplianceSoftware && view === "appliance");
    const { changelogs: cChangelogs, isLoading: isCLoading, isFetching: isCFetching, error: cError, refetch: refetchC } = useChangelogs(limit, undefined, "blast-c", canLoadChangelogs && view === "blast");
    const { changelogs: cppChangelogs, isLoading: isCppLoading, isFetching: isCppFetching, error: cppError, refetch: refetchCpp } = useChangelogs(limit, undefined, "blast-cpp", canLoadChangelogs && view === "blast");
    const blastChangelogs = [
        ...cChangelogs,
        ...cppChangelogs
    ].sort((a, b)=>b.releaseDate.localeCompare(a.releaseDate));
    const displayedChangelogs = view === "appliance" ? applianceChangelogs : blastChangelogs;
    const isLoading = view === "appliance" ? isApplianceLoading : isCLoading || isCppLoading;
    const isFetching = view === "appliance" ? isApplianceFetching : isCFetching || isCppFetching;
    const error = view === "appliance" ? applianceError : cError ?? cppError;
    const downloadMutation = useDownloadChangelog();
    const handleDownload = async (changelog)=>{
        if (isDownloading) return;
        if (!changelog.changelogId) {
            toast.error("Changelog ID is missing");
            return;
        }
        setIsDownloading(true);
        try {
            const result = await downloadMutation.mutateAsync(changelog);
            if (result.downloadUrl) {
                try {
                    const response = await fetch(result.downloadUrl);
                    if (!response.ok) throw new Error("Network response was not ok");
                    const blob = await response.blob();
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = result.fileName || "file";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(link.href);
                    toast.success("Download completed");
                } catch (err) {
                    console.error("Download failed:", err);
                    toast.error("Failed to download file");
                }
            }
        } catch  {
            toast.error("Failed to generate download URL");
        } finally{
            setIsDownloading(false);
        }
    };
    const handleRefresh = async ()=>{
        try {
            const results = view === "appliance" ? [
                await refetchAppliance()
            ] : await Promise.all([
                refetchC(),
                refetchCpp()
            ]);
            if (results.some((result)=>result.isError)) {
                throw new Error("Failed to refresh changelogs");
            }
            toast.success("Changelogs refreshed");
        } catch  {
            toast.error("Failed to refresh changelogs");
        }
    };
    const handleViewChange = (nextView)=>{
        setSelectedView(nextView);
        setExpandedSections(new Set());
    };
    const toggleExpand = (changelogId)=>{
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(changelogId)) {
            newExpanded.delete(changelogId);
        } else {
            newExpanded.add(changelogId);
        }
        setExpandedSections(newExpanded);
    };
    const renderContent = ()=>{
        if (isLoading && displayedChangelogs.length === 0) {
            return /*#__PURE__*/ _jsx(DashboardPageLoading, {});
        }
        if (error && displayedChangelogs.length === 0) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "bg-red-50 border border-red-200 rounded-lg p-4",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-red-800 font-medium",
                        children: "Failed to load changelogs"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-red-600 text-sm mt-1",
                        children: error instanceof Error ? error.message : "An unexpected error occurred"
                    })
                ]
            });
        }
        if (displayedChangelogs.length === 0) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border border-gray-200 rounded-lg p-12 text-center",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-lg font-medium text-gray-900 mb-2",
                        children: "No changelogs available"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "There are no changelogs to display at this time."
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                error && /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    className: "rounded-lg border border-amber-200 bg-amber-50 p-4",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "font-medium text-amber-900",
                            children: "Some changelogs could not be loaded"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "mt-1 text-sm text-amber-700",
                            children: "The releases shown below may be incomplete. Try refreshing the page."
                        })
                    ]
                }),
                displayedChangelogs.map((changelog, index)=>/*#__PURE__*/ _jsx(ChangelogCard, {
                        changelog: changelog,
                        isExpanded: expandedSections.has(changelog.changelogId),
                        onToggleExpand: ()=>toggleExpand(changelog.changelogId),
                        onDownload: handleDownload,
                        isDownloading: isDownloading,
                        isLatest: index === 0
                    }, changelog.changelogId))
            ]
        });
    };
    const refreshAction = /*#__PURE__*/ _jsxs(Button, {
        onClick: handleRefresh,
        disabled: isProductAccessPending || isFetching,
        variant: "simple",
        color: "navy-90",
        className: "inline-flex items-center gap-2 whitespace-nowrap !px-4 !py-2 text-sm",
        children: [
            /*#__PURE__*/ _jsx(RefreshCw, {
                className: `h-4 w-4 ${isFetching ? "animate-spin" : ""}`
            }),
            "Check for Updates"
        ]
    });
    return /*#__PURE__*/ _jsx(DashboardPageLayout, {
        title: "Changelogs",
        description: "Download Appliance Software and BLAST releases",
        actions: refreshAction,
        children: isProductAccessPending ? /*#__PURE__*/ _jsx(DashboardPageLoading, {}) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(DistributionTabs, {
                    value: view,
                    onChange: handleViewChange,
                    showApplianceSoftware: canAccessApplianceSoftware
                }),
                /*#__PURE__*/ _jsx("div", {
                    role: "tabpanel",
                    id: "changelog-panel",
                    "aria-labelledby": `changelog-${view}-tab`,
                    children: renderContent()
                })
            ]
        })
    });
}
